// store.jsx — QuotationByIdMultiRoleContext (REST + WS hub genérico + anti-parpadeo)
import { createContext } from "preact";
import { useContext, useMemo, useState, useCallback, useEffect, useRef } from "preact/hooks";
import { fetchToServer } from "@shared/components";
import { getWsHub } from "@shared/components";

// -------- util: URL REST --------
function makeUrl(role, id) {
  return `/${role}/api/quotations/${id}`;
}

// -------- util: comparación para evitar renders innecesarios --------
function pickComparable(q) {
  if (!q) return null;
  return {
    id: q._id || q.id || q.idQuotation || null,
    updatedAt: q.updatedAt || q.updated_at || null,
    status: q?.status?.name || null,
    reqs: Array.isArray(q.requirements)
      ? q.requirements.map((r) => ({
          id: r?._id || r?.id || null,
          s: r?.status?.name || null,
          d: r?.description || "",
        }))
      : [],
    attachmentsLen: Array.isArray(q.attachments) ? q.attachments.length : 0,
    aiReviewsLen: Array.isArray(q.aiReviews) ? q.aiReviews.length : 0,
    resultsLen: Array.isArray(q.results) ? q.results.length : 0,
  };
}
function isSameQuotation(a, b) {
  const A = pickComparable(a);
  const B = pickComparable(b);
  return JSON.stringify(A) === JSON.stringify(B);
}

// -------- util: merge de "patch" entrante (shallow, arrays reemplazo) --------
function mergePatch(base, patch) {
  if (!patch) return base;
  const out = { ...(base || {}) };
  for (const k of Object.keys(patch)) {
    const pv = patch[k];
    if (Array.isArray(pv)) out[k] = pv.slice();
    else if (pv && typeof pv === "object") out[k] = { ...(out[k] || {}), ...pv };
    else out[k] = pv;
  }
  return out;
}

export const QuotationByIdMultiRoleContext = createContext(null);

export function QuotationByIdMultiRoleProvider({ quotationId, role = "adevs-client", children }) {
  const [currentRole, setRole] = useState(role);
  const [currentId, setQuotationId] = useState(quotationId);

  const [quotation, setQuotation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const prevRef = useRef(null);
  const wsBoundRef = useRef({ id: null });
  const patchQueueRef = useRef([]);
  const rafRef = useRef(null);

  // Sync props -> estado
  useEffect(() => setRole(role), [role]);
  useEffect(() => setQuotationId(quotationId), [quotationId]);

  // -------- carga REST --------
  const load = useCallback(
    async (id = currentId, r = currentRole, { signal, silent = false } = {}) => {
      if (!id) throw new Error("quotationId missing");
      silent ? setIsRefreshing(true) : setIsLoading(true);
      setError(null);
      try {
        const data = await fetchToServer({
          url: makeUrl(r, id),
          method: "get",
          withCredentials: true,
          signal,
        });
        const q =
          data?.data?.quotation ??
          data?.data?.item ??
          data?.data ??
          data?.quotation ??
          data;

        const prev = prevRef.current;
        if (!isSameQuotation(prev, q)) {
          setQuotation(q);
          prevRef.current = q;
        }
        return q;
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        silent ? setIsRefreshing(false) : setIsLoading(false);
      }
    },
    [currentId, currentRole]
  );

  // -------- WebSocket: suscripción por quotationId vía Hub genérico --------
  useEffect(() => {
    if (!currentId) return;

    const hub = getWsHub();
    // Namespace reutilizable: mañana puedes usar "/ws/messages", "/ws/notifications", etc.
    const socket = hub.ns("/ws/quotations");

    const thisBind = { id: currentId };
    wsBoundRef.current = thisBind;

    // Suscribirse a la sala de esta cotización
    socket.emit("subscribe", { quotationId: currentId });

    const onPatch = (patch) => {
      // Ignora si cambiaste de cotización en caliente
      if (wsBoundRef.current.id !== currentId) return;

      patchQueueRef.current.push(patch);
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        const queue = patchQueueRef.current;
        patchQueueRef.current = [];
        rafRef.current = null;

        if (!queue.length) return;
        const merged = queue.reduce((acc, p) => mergePatch(acc, p), {});
        setQuotation((prev) => {
          const next = mergePatch(prev, merged);
          if (!isSameQuotation(prev, next)) {
            prevRef.current = next;
            return next;
          }
          return prev;
        });
      });
    };

    socket.on("quotation:patch", onPatch);

    return () => {
      socket.emit("unsubscribe", { quotationId: currentId });
      socket.off("quotation:patch", onPatch);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      patchQueueRef.current = [];
      rafRef.current = null;
    };
  }, [currentId]);

  // -------- carga inicial --------
  useEffect(() => {
    if (!currentId) return;
    const ctrl = new AbortController();
    load(currentId, currentRole, { signal: ctrl.signal }).catch(() => {});
    return () => ctrl.abort();
  }, [currentId, currentRole, load]);

  // refresh normal o silencioso: refresh({ silent:true })
  const refresh = useCallback((opts) => load(undefined, undefined, opts), [load]);

  const value = useMemo(
    () => ({
      role: currentRole,
      quotationId: currentId,
      quotation,
      isLoading,
      isRefreshing,
      error,
      isReady: !!quotation && !isLoading && !error,
      refresh,
      setRole,
      setQuotationId,
      load,
    }),
    [currentRole, currentId, quotation, isLoading, isRefreshing, error, refresh, load]
  );

  return (
    <QuotationByIdMultiRoleContext.Provider value={value}>
      {children}
    </QuotationByIdMultiRoleContext.Provider>
  );
}

export function useQuotationByIdMultiRole() {
  const ctx = useContext(QuotationByIdMultiRoleContext);
  if (!ctx) throw new Error("useQuotationByIdMultiRole must be used within QuotationByIdMultiRoleProvider");
  return ctx;
}
